import { useContext, useEffect, useState } from "react";
import axios from "axios";

// Api
import { GetActivePopups } from "../../Api/PopUpApi";

// Context
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";

// Models & Helpers
import type { ActivePopup } from "../../Models/DTOs/ActivePopupDTO";
import { isDismissed } from "../../Helpers/PopupDismissal";

/**
 * Fetches the active popups for the given path and resolves the single winner:
 * the highest-priority eligible popup the user hasn't already dismissed.
 *
 * Refetches whenever the path or login state changes. The winner is cleared
 * immediately on each change so a stale popup never lingers across navigation.
 */
function useActivePopups(pathname: string): { winner: ActivePopup | null } {
    const authCtx = useContext(UserAuthenticationContext);
    const isLoggedIn = authCtx.doesAuthTokenExist();

    const [winner, setWinner] = useState<ActivePopup | null>(null);

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();

        // Clear any previous winner up-front so the manager closes a popup that
        // no longer applies to the new page before the new fetch resolves.
        setWinner(null);

        (async () => {
            const token = isLoggedIn ? await authCtx.getValidAccessToken() : null;
            if (cancelled) return;

            const res = await GetActivePopups(pathname, token, controller);
            if (cancelled) return;

            if (axios.isAxiosError(res)) {
                setWinner(null);
                return;
            }

            // Items arrive highest-priority first; the winner is simply the
            // first one the user hasn't dismissed.
            const next = res.items.find((p) => !isDismissed(p.popupId)) ?? null;
            setWinner(next);
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
        // authCtx methods are stable refs; refetch only on path / login changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, isLoggedIn]);

    return { winner };
}

export default useActivePopups;
